import { Flex, Spinner } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineCameraStreamViewer } from 'components/machine/camera-stream-viewer';
import {
  StreamingContext,
  StreamingProvider,
} from 'components/machine/camera-stream-viewer/context';
import { MachineContext } from 'contexts/machine.context';
import { addSeconds, isFuture } from 'date-fns';
import { t } from 'i18next';
import { IBaseDialog, IFullDialog } from 'interfaces/i-dialogs';
import { StaticVideoType, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IBallStatusMsg, IReadyMsg } from 'lib_ts/interfaces/i-machine-msg';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { WebSocketService } from 'services/web-socket.service';

const COMPONENT_NAME = 'MachineInspectionDialog';

const EJECT_BALL_RPM = 400;

const EJECT_BALL_YAW = 2;

// how many seconds into the future should the app wait before acting upon any r2f from the machine after sending the ejection mstarget
const MIN_WAIT_BEFORE_EJECTING_SEC = 2;

interface IProps extends IBaseDialog {
  // might differ from machine context (e.g. admins inspecting others' machines)
  machineID: string;

  // 'other' is only for admins
  mode: 'self' | 'other';
}

export const MachineInspectionDialogHoC = (props: IProps) => {
  return (
    <StreamingProvider machineID={props.machineID}>
      <MachineInspectionDialog {...props} />
    </StreamingProvider>
  );
};

const MachineInspectionDialog = (props: IProps) => {
  const { paused, lastFrame } = useContext(StreamingContext);

  // should only be used when props.mode === 'self'
  const { machine, sendTarget, specialMstarget, setSpecialMode } =
    useContext(MachineContext);

  const autoCloseTimeout = useRef<NodeJS.Timeout>();
  const dbCooldownTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      clearTimeout(autoCloseTimeout.current);
      clearTimeout(dbCooldownTimeout.current);
    };
  }, []);

  const [ejecting, setEjecting] = useState(false);
  // skips r2f until after this date
  const [ignoreUntil, setIgnoreUntil] = useState(new Date());
  // prevents additional drop ball triggers until a ball-status is received
  const [awaiting, setAwaiting] = useState(false);

  useEffect(() => {
    if (props.mode === 'self') {
      setSpecialMode('inspect-machine');
    }

    return () => {
      if (props.mode === 'self') {
        setSpecialMode(undefined);
      }
    };
  }, [props.mode]);

  const statusCallback = useCallback(
    (event: CustomEvent) => {
      if (ejecting) {
        // ignore ball status updates while ejecting
        return;
      }

      const data: IBallStatusMsg = event.detail;

      if (data.ball_count !== 1) {
        setAwaiting(false);
        return;
      }

      autoCloseTimeout.current = setTimeout(() => {
        props.onClose();
      }, 3_000);
    },
    [ejecting]
  );

  useEffect(() => {
    WebSocketHelper.on(WsMsgType.M2U_BallStatus, statusCallback);
    return () =>
      WebSocketHelper.remove(WsMsgType.M2U_BallStatus, statusCallback);
  }, [statusCallback]);

  const r2fCallback = useCallback(
    (event: CustomEvent) => {
      if (props.mode !== 'self') {
        return;
      }

      if (!ejecting) {
        return;
      }

      if (isFuture(ignoreUntil)) {
        return;
      }

      const r2f: IReadyMsg = event.detail;

      if (!r2f.data) {
        return;
      }

      if (!r2f.data.w1 || !r2f.data.w2 || !r2f.data.w3) {
        return;
      }

      if (!r2f.data.px || !r2f.data.pz) {
        return;
      }

      if (!r2f.data.yaw) {
        return;
      }

      NotifyHelper.warning({
        message_md: 'Attempting to eject ball(s) from inserter, please wait...',
      });

      setEjecting(false);
      WebSocketService.send(WsMsgType.U2S_EjectBalls, {}, COMPONENT_NAME);
    },
    [props.mode, ejecting, ignoreUntil]
  );

  useEffect(() => {
    WebSocketHelper.on(WsMsgType.M2U_ReadyToFire, r2fCallback);
    return () => WebSocketHelper.remove(WsMsgType.M2U_ReadyToFire, r2fCallback);
  }, [r2fCallback]);

  const dialogProps: IFullDialog = {
    identifier: COMPONENT_NAME,
    width: RADIX.DIALOG.WIDTH.LG,
    title: 'main.inspect-machine',
    content: (
      <Flex direction="column" gap={RADIX.FLEX.GAP.LG}>
        <MachineCameraStreamViewer machineID={props.machineID} />
        {ejecting && <CommonCallout text="Ejecting balls, please wait..." />}
      </Flex>
    ),
    buttons: [
      {
        label: 'main.drop-ball',
        icon: awaiting ? <Spinner /> : undefined,
        color: RADIX.COLOR.WARNING,
        disabled: awaiting || props.mode === 'other',
        onClick: () => {
          if (awaiting) {
            // prevent spamming of drop ball
            return;
          }

          clearTimeout(dbCooldownTimeout.current);

          setAwaiting(true);

          MachineContextHelper.sendDropBall({
            machineID: props.machineID,
            source: COMPONENT_NAME,
          });

          NotifyHelper.info({
            message_md: 'Attempting to drop ball, please wait...',
            hideHeader: true,
          });

          // machine throttles drop ball requests after two attempts within 10 seconds and stops providing status updates
          // re-enable the drop ball button after a cooldown so we don't end up with a permanently-disabled button
          dbCooldownTimeout.current = setTimeout(() => {
            setAwaiting(false);
          }, 15_000);
        },
      },
      {
        label: 'main.eject-balls',
        color: RADIX.COLOR.WARNING,
        // only the active user the machine being inspected can eject balls
        invisible: props.mode !== 'self',
        tooltip: paused
          ? 'Please resume the stream before ejecting'
          : !lastFrame
          ? 'Please wait for the stream to load before ejecting'
          : undefined,
        disabled: paused || !lastFrame,
        onClick: async () => {
          if (ejecting) {
            return;
          }

          const data = MachineContextHelper.getTroubleshootingMS(machine, {
            id: 'eject-balls',
            video_uuid: StaticVideoType.screensaver,
            wheels: {
              w1: EJECT_BALL_RPM,
              w2: EJECT_BALL_RPM,
              w3: EJECT_BALL_RPM,
            },
            yaw: EJECT_BALL_YAW,
            rapid: true,
          });

          const ignoreUntil = addSeconds(
            new Date(),
            MIN_WAIT_BEFORE_EJECTING_SEC
          );

          setIgnoreUntil(ignoreUntil);

          const sendResult = await sendTarget({
            source: COMPONENT_NAME,
            pitch: data.pitch,
            msMsg: data.msg,
            force: true,
          });

          if (!sendResult.success) {
            NotifyHelper.warning({
              message_md: t('common.request-failed-msg'),
            });
            return;
          }

          setEjecting(true);
        },
      },
      {
        label: 'common.lower-machine',
        color: RADIX.COLOR.WARNING,
        disabled: !lastFrame,
        onClick: () => specialMstarget(SpecialMsPosition.lowered),
      },
    ],
    onClose: props.onClose,
  };

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <CommonDialog {...dialogProps} />
    </ErrorBoundary>
  );
};
